import { useState } from 'react'
import { useSnackbar } from 'notistack'
import { useRecoilState, useRecoilValue } from 'recoil'
import styled from 'styled-components'

import { usePostStartPayment } from '~/features/payment/queries/usePayment'
import { PaymentDivideType } from '~/features/payment/types/payment'
import { useTicketDetail } from '~/features/ticket/queries/ticket'
import {
  getTicketAmountColor,
  getTicketDetailBackgroundImage,
} from '~/features/ticket/utils/ticket'
import Close from '~/shared/assets/icons/Close'
import SnackbarRest from '~/shared/assets/icons/SnackbarRest'
import LoadingScreen from '~/shared/components/loading/LoadingScreen'
import useViewport from '~/shared/hooks/useViewport'
import {
  isModalOpenState,
  isPaymentModalOpenState,
  ticketDetailParamsState,
} from '~/shared/states'
import theme from '~/shared/styles/theme'
import { fCurrency } from '~/shared/utils/format-number'

function TicketDetailModal() {
  const viewport = useViewport()
  const { enqueueSnackbar } = useSnackbar()
  const isMobile = viewport < theme.deviceSizes.medium

  const [quantity, setQuantity] = useState(0)
  const [amenitiesQuantity, setAmenitiesQuantity] = useState(0)
  const [totalAmount, setTotalAmount] = useState(0)

  const [_, setIsModalOpen] = useRecoilState(isModalOpenState)
  const ticketDetailParams = useRecoilValue(ticketDetailParamsState)
  const { eventTicketId } = ticketDetailParams

  const { data, isLoading } = useTicketDetail(eventTicketId)

  const [isPaymentModalOpen, setIsPaymentModalOpen] = useRecoilState(
    isPaymentModalOpenState,
  )

  const { mutate: paymentStartMutate } = usePostStartPayment()

  if (isLoading) return <LoadingScreen />

  const { ticket_info, event_ticket_info } = data!

  if (!event_ticket_info) {
    return
  }

  // GGUF 입장권 일 경우 편의시설 옵션 선택 불가
  const isGguf = ticket_info.admission_type === 'E0200001'

  const discountRate =
    event_ticket_info.discount_amount > 0
      ? Math.ceil(
          (event_ticket_info.discount_amount / event_ticket_info.amount) * 100,
        )
      : 0
  const discountedAmount =
    event_ticket_info.amount - event_ticket_info.discount_amount

  const handleIncrement = () => {
    if (quantity < event_ticket_info.max_user_buy) {
      setQuantity(quantity + 1)
      setTotalAmount(totalAmount + discountedAmount)
    }
  }

  const handleDecrement = () => {
    if (quantity > 0) {
      setQuantity(quantity - 1)
      if (!isGguf && amenitiesQuantity > quantity - 1) {
        setAmenitiesQuantity(quantity - 1)
        setTotalAmount(
          totalAmount - discountedAmount - event_ticket_info.option_1_amount,
        )
      } else {
        setTotalAmount(totalAmount - discountedAmount)
      }
    }
  }

  const handleAmenitiesIncrement = () => {
    if (amenitiesQuantity < quantity) {
      setAmenitiesQuantity(amenitiesQuantity + 1)
      setTotalAmount(totalAmount + event_ticket_info.option_1_amount)
    }
  }

  const handleAmenitiesDecrement = () => {
    if (amenitiesQuantity > 0) {
      setAmenitiesQuantity(amenitiesQuantity - 1)
      setTotalAmount(totalAmount - event_ticket_info.option_1_amount)
    }
  }

  const handleClickBuy = () => {
    paymentStartMutate(
      {
        paymentInfo: {
          goods_id: eventTicketId,
          divide: PaymentDivideType.TICKET,
          quantity: quantity,
          option_1_quantity: amenitiesQuantity,
        },
      },
      {
        onSuccess: (res) => {
          if (!res.errorMsg) {
            setIsPaymentModalOpen({
              contents: {
                productName: '일러스타 페스 입장권',
                amount: res.data.payment_info.total_amount,
                orderId: res.data.payment_info.order_code,
                userId: res.data.payment_info.user_id,
                paymentKey: res.data.payment_info.payment_key,
                paymentType: res.data.payment_info.payment_type,
              },
              isOpen: true,
            })
            setIsModalOpen({ contents: '', isOpen: false })
          } else {
            enqueueSnackbar(res.errorMsg, { variant: 'error' })
          }
        },
      },
    )
  }

  return (
    <Wrapper
      $isPaymentModalOpen={isPaymentModalOpen.isOpen}
      onClick={(e) => e.stopPropagation()}
    >
      <CloseIcon
        onClick={() => setIsModalOpen({ contents: '', isOpen: false })}
      />
      <TicketBox
        $backgroundImage={getTicketDetailBackgroundImage(
          ticket_info.admission_type,
          isMobile,
        )}
      >
        {discountRate > 0 && (
          <TicketSaleTag>{`${discountRate}% 할인`}</TicketSaleTag>
        )}
        <TicketTitle>{ticket_info.description}</TicketTitle>
        <TicketAmountTag
          $color={getTicketAmountColor(ticket_info.admission_type)}
        >
          {fCurrency(discountedAmount)}
        </TicketAmountTag>
      </TicketBox>
      {event_ticket_info.max_user_buy === 0 ? (
        <SoldOutMessage>
          <SoldOutIcon color="white" />
          {/* <SnackbarRest /> */}
          최대 구매수량을 초과하여 더 이상 구매할 수 없습니다.
        </SoldOutMessage>
      ) : (
        <>
          <TicketDescBox>
            <TicketDescTitle>입장권 옵션 선택하기</TicketDescTitle>
            <TicketDesc>
              요일 및 수량을 선택해주세요. *GGUF 입장권은 토/일 중 하루만
              구매가능합니다.
            </TicketDesc>
          </TicketDescBox>
          <TicketOptionBox>
            <TicketOptionDay>{`${ticket_info.description} (${event_ticket_info.description})`}</TicketOptionDay>
            <Counter>
              <CounterButton
                onClick={handleDecrement}
                disabled={quantity === 0}
              >
                -
              </CounterButton>
              <Quantity $disabled={quantity === 0}>{quantity}</Quantity>
              <CounterButton
                onClick={handleIncrement}
                disabled={quantity >= event_ticket_info.max_user_buy}
              >
                +
              </CounterButton>
            </Counter>
          </TicketOptionBox>
          {!isGguf && (
            <TicketOptionBox>
              <TicketOptionDay>편의시설 추가</TicketOptionDay>
              <Counter>
                <CounterButton
                  onClick={handleAmenitiesDecrement}
                  disabled={amenitiesQuantity === 0}
                >
                  -
                </CounterButton>
                <Quantity $disabled={amenitiesQuantity === 0}>
                  {amenitiesQuantity}
                </Quantity>
                <CounterButton
                  onClick={handleAmenitiesIncrement}
                  disabled={amenitiesQuantity >= quantity}
                >
                  +
                </CounterButton>
              </Counter>
            </TicketOptionBox>
          )}
          <DotLine />
          <TotalAmountBox>
            <TotalAmount>{`총 ${fCurrency(totalAmount)}`}</TotalAmount>
            <BuyButton
              onClick={() => (quantity > 0 ? handleClickBuy() : null)}
              $disabled={quantity === 0}
            >
              구매하기
            </BuyButton>
          </TotalAmountBox>
        </>
      )}
    </Wrapper>
  )
}

export default TicketDetailModal

const Wrapper = styled.section<{ $isPaymentModalOpen: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  display: flex;
  flex-direction: column;
  gap: 36px;

  box-sizing: border-box;
  width: 896px;
  height: auto;
  margin: 0 auto;
  padding: 108px 77px 60px;

  background-color: ${theme.colors.white};
  border-radius: 12px;

  @media ${theme.device.medium} {
    gap: 24px;
    width: 90%;
    height: auto;
    padding: 56px 20px 30px;
  }
`

const CloseIcon = styled(Close)`
  position: absolute;
  top: 36px;
  right: 36px;

  @media ${theme.device.medium} {
    top: 20px;
    right: 20px;
    width: 24px;
    height: 24px;
  }
`

const TicketBox = styled.div<{ $backgroundImage: string }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: start;
  justify-content: center;

  box-sizing: border-box;
  width: 741px;
  height: 274px;
  padding-left: 98px;

  background-image: url(${(props) => props.$backgroundImage});
  filter: drop-shadow(6px 7px 8px #c3c3c3);

  @media ${theme.device.medium} {
    gap: 4px;
    width: 100%;
    height: 169px;
    padding-left: 33px;
  }
`

const TicketSaleTag = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 77px;
  height: 21px;

  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 15px;
  font-weight: 700;
  line-height: 21px;
  color: white;

  background-color: #fa6b6b;
`

const TicketTitle = styled.h3`
  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 42px;
  font-weight: 700;
  line-height: 58px;

  @media ${theme.device.medium} {
    font-size: 21px;
    line-height: 29px;
  }
`

const TicketAmountTag = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;

  box-sizing: border-box;
  width: fit-content;
  height: 37px;
  padding: 10px;

  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 25px;
  color: ${(props) => props.$color};

  background-color: white;
  border-radius: 8px;
`

const TicketDescBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const TicketDescTitle = styled.h3`
  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 32px;
  font-weight: 700;
  line-height: 44px;

  @media ${theme.device.medium} {
    font-size: 21px;
    line-height: 29px;
  }
`
const TicketDesc = styled.p`
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: #838685;
`

const TicketOptionBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media ${theme.device.medium} {
    gap: 4px;
    align-items: center;
    width: 100%;
  }
`

const TicketOptionDay = styled.span`
  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 21px;
  font-weight: 700;
  line-height: 29px;
  color: #313634;

  @media ${theme.device.medium} {
    font-size: 18px;
    line-height: 25px;
  }
`

const Counter = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;

  @media ${theme.device.medium} {
    gap: 2px;
  }
`

const CounterButton = styled.button`
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 53px;
  height: 53px;

  font-size: 20px;

  background-color: #fff;
  border: 1px solid #c1c3c2;
  border-radius: 12px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  @media ${theme.device.medium} {
    width: 40px;
    height: 40px;
  }
`

const Quantity = styled.div<{ $disabled: boolean }>`
  width: 40px;

  font-size: 16px;
  font-weight: 700;
  color: ${(props) => (props.$disabled ? '#C1C3C2' : '#313634')};
  text-align: center;
`

const DotLine = styled.div`
  width: 100%;
  height: 100%;
  border: 3px #c1c3c2 dotted;
`

const TotalAmountBox = styled.div`
  display: flex;
  justify-content: space-between;
`

const TotalAmount = styled.span`
  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 32px;
  font-weight: 700;
  line-height: 44px;

  @media ${theme.device.medium} {
    font-size: 24px;
    line-height: 33px;
  }
`

const BuyButton = styled.button<{ $disabled: boolean }>`
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};

  width: 141px;
  height: 58px;

  font-size: 24px;
  font-weight: 500;
  line-height: 33px;
  color: white;

  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
  background-color: ${(props) => (props.$disabled ? '#C1C3C2' : '#313634')};
  border: none;

  @media ${theme.device.medium} {
    width: 110px;
    height: 48px;
    font-size: 18px;
    line-height: 25px;
  }
`

const SoldOutMessage = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;
  padding: 12px;

  font-size: 18px;
  font-weight: 700;
  color: white;

  /* color: #fa6b6b; */
  text-align: center;

  background-color: #fa6b6b;
  border-radius: 20px;

  @media ${theme.device.medium} {
    gap: 4px;
    padding: 8px;
    font-size: 12px;
  }
`

const SoldOutIcon = styled(SnackbarRest)`
  width: 20px;
  height: 20px;

  @media ${theme.device.medium} {
    width: 16px;
    height: 16px;
  }
`
